/**
 * The `GridView` that grid.ts asks about, built over a run's current floor and
 * units. Down party units are not on the grid for this view: they neither
 * block movement nor get targeted.
 */
import { idiv } from './determinism.ts';
import type { GridView, Pos } from './grid.ts';
import { inBounds, reachableWithin } from './grid.ts';
import type { Floor, Room, RunState, Tile } from './state.ts';
import { floorOf } from './state.ts';

/** The tile at `pos`, or `wall` outside the grid. */
export const tileAt = (floor: Floor, pos: Pos): Tile =>
  inBounds(floor, pos) ? (floor.tiles[pos.y * floor.width + pos.x] ?? 'wall') : 'wall';

/** True when a unit can stand on the tile (floor or exit). */
export const walkable = (floor: Floor, pos: Pos): boolean => tileAt(floor, pos) !== 'wall';

export const inRoom = (room: Room, pos: Pos): boolean =>
  pos.x >= room.x && pos.y >= room.y && pos.x < room.x + room.w && pos.y < room.y + room.h;

/** The index of the room containing `pos`, or null in corridors. */
export const roomIndexAt = (rooms: readonly Room[], pos: Pos): number | null => {
  const i = rooms.findIndex((r) => inRoom(r, pos));
  return i < 0 ? null : i;
};

/** The centre tile of a room (rounded towards its origin). */
export const roomCenter = (r: Room): Pos => ({ x: r.x + idiv(r.w - 1, 2), y: r.y + idiv(r.h - 1, 2) });

/** Interior tiles of a room, row-major. */
export const tilesOfRoom = (room: Room): Pos[] => {
  const out: Pos[] = [];
  for (let y = room.y; y < room.y + room.h; y += 1) for (let x = room.x; x < room.x + room.w; x += 1) out.push({ x, y });
  return out;
};

const key = (p: Pos): string => `${p.x},${p.y}`;

/** A view of the state's floor for grid.ts. Throws when there is no floor. */
export const gridView = (state: RunState): GridView => {
  const floor = floorOf(state);
  const occupied: Record<string, string> = {};
  for (const u of state.units) if (!u.down) occupied[key(u.pos)] = u.id;
  return {
    width: floor.width,
    height: floor.height,
    isFloor: (p) => walkable(floor, p),
    unitAt: (p) => occupied[key(p)] ?? null,
    roomAt: (p) => roomIndexAt(floor.rooms, p),
    roomTiles: (room) => {
      const r = floor.rooms[room];
      return r ? tilesOfRoom(r) : [];
    },
  };
};

/**
 * Tiles within `range` orthogonal steps of `from` over non-wall tiles
 * (grid.ts `reachableWithin`): where a `single` skill of that range can reach
 * from `from`. Exported so a client can list candidate targets without
 * repeating the rule.
 */
export const reachableFrom = (state: RunState, from: Pos, range: number): Pos[] => reachableWithin(gridView(state), from, range);
